<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="webinars-swiper__inner">
        <div ref="swiperEl" class="swiper webinars-swiper__swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(item, index) in webinarsList"
              :key="item.id"
              :class="[
                'swiper-slide webinars-swiper__slide',
                {
                  'webinars-swiper__slide--left': index % 2 === 0,
                  'webinars-swiper__slide--right': index % 2 === 1,
                },
              ]"
            >
              <site-link
                :link="{
                  value: {
                    href: item.fields.url.value.href,
                    linktype: 'internal',
                    text: item.fields.webinarsTitle.value,
                  },
                }"
                class="webinars-swiper__card"
              >
                <AdaptiveImage
                  class="webinars-swiper__card-bg"
                  :field="{ fields: item?.fields?.webinarsImage?.value }"
                  :isBackground="true"
                  :lazy-load="false"
                  :enableScrollDetection="false"
                  :show="true"
                >
                  <JssRichText
                    class="webinars-swiper__card-online"
                    :field="item?.fields?.onlineText"
                  />
                  <JssRichText
                    class="webinars-swiper__card-desc"
                    :field="item?.fields?.webinarsCardDesc"
                  />
                  <div class="webinars-swiper__card-date">{{ formatWebinarsDateTime(item) }}</div>
                </AdaptiveImage>
                <div class="webinars-swiper__card-text">
                  <JssRichText
                    class="webinars-swiper__card-subtitle"
                    :field="item?.fields?.webinarsTitle"
                  />
                </div>
              </site-link>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="webinars-swiper__control webinars-swiper__control--prev"
          :disabled="!hasPrev"
          aria-label="Previous"
          @click="goToPrev"
        >
          <Icon name="web-prev" />
        </button>
        <button
          type="button"
          class="webinars-swiper__control webinars-swiper__control--next"
          :disabled="!hasNext"
          aria-label="Next"
          @click="goToNext"
        >
          <Icon name="web-next" />
        </button>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import Swiper from 'swiper';
import { destroySwiper } from '@/utils/site-utils';
import { getBetterUrl } from '@/utils/uri-utils';
import Icon from '@/elements/Icon.vue';
import utils from '@/utils';
import api from '@/api';

export default {
  name: 'WebinarsSwiper',
  components: {
    Icon,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const router = useRouter();
    const webinarsList = ref([]);
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'webinars-swiper'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const desktopSlidesPerView = computed(() => {
      const viewSlidesValue = props.fields?.viewSlides?.value;
      const parsed = parseInt(viewSlidesValue, 10);
      return isNaN(parsed) || parsed <= 0 ? 2 : parsed;
    });

    let swiper = null;
    const swiperEl = ref(null);

    const getWebinars = async () => {
      const [res, ex] = await api.news.getNewsDetail({
        type: 'webinar',
        id: props.page.itemId,
      });
      if (ex) {
        webinarsList.value = [ex.previous, ex.next].filter(Boolean);
      }
    };

    const formatWebinarsDateTime = (item) => {
      const date = item?.fields?.webinarsDate?.value;
      const time = item?.fields?.webinarsTime?.value;
      const formattedDate = utils.date.formatDateWithOrdinal(date);
      if (formattedDate && time) {
        return `${formattedDate} ${time}`;
      }
      return formattedDate || time || '';
    };

    const hasPrev = computed(() => !!webinarsList.value[0]);
    const hasNext = computed(() => !!webinarsList.value[1]);

    const buildItemUrl = (item) => {
      if (!item?.url) return '';
      const { lang = 'en' } = router.currentRoute.value?.params ?? {};
      return getBetterUrl(item.fields.url.value.href, lang) ?? '';
    };

    const goToPrev = () => {
      const url = buildItemUrl(webinarsList.value[0]);
      if (url) router.push(url);
    };

    const goToNext = () => {
      const url = buildItemUrl(webinarsList.value[1]);
      if (url) router.push(url);
    };

    const buildSwiper = () => {
      if (!swiperEl.value || !webinarsList.value.length) return;

      destroySwiper(swiper, true, true);
      const isSingle = webinarsList.value.length <= 1;
      swiper = new Swiper(swiperEl.value, {
        slidesPerView: 1,
        spaceBetween: 24,
        centeredSlides: false,
        watchOverflow: true,
        resizeObserver: true,
        observer: true,
        observeParents: true,
        breakpoints: isSingle
          ? {}
          : {
              744: {
                slidesPerView: desktopSlidesPerView.value,
              },
            },
      });
    };

    watch(
      () => webinarsList.value.length,
      // eslint-disable-next-line vue/valid-next-tick
      () => nextTick(() => buildSwiper())
    );

    onMounted(() => {
      getWebinars();
    });

    onBeforeUnmount(() => {
      destroySwiper(swiper, true, true);
    });

    return {
      rootClass,
      rootStyle,
      formatWebinarsDateTime,
      webinarsList,
      swiperEl,
      hasPrev,
      hasNext,
      goToPrev,
      goToNext,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.webinars-swiper {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &__inner {
    position: relative;
    width: 100%;
    overflow: hidden;
  }

  &__swiper {
    overflow: visible;
    padding: 0;
  }

  &__slide {
    height: auto;
  }

  &__card {
    position: relative;
    display: flex;
    align-items: center;
    overflow: hidden;
    background: #f5f5f5;
    isolation: isolate;
    padding: 20px 65px;

    &-bg {
      width: 120px;
      aspect-ratio: 16 / 11;
      position: relative;
      flex-shrink: 0;

      > div,
      .e-background-image,
      .e-background-image__img,
      img {
        width: 100%;
        height: 100%;
      }

      img {
        display: block;
        object-fit: cover;
      }
    }

    &-text {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-left: 20px;

      & > * {
        margin: 0;
      }
    }

    &-online {
      position: absolute;
      top: 8px;
      left: 8px;
      z-index: 2;
      font-size: 8px;
      font-weight: 600;
      color: $white;
      background: rgba(107, 145, 113, 0.9);
      padding: 2px 8px;
    }

    &-desc {
      position: absolute;
      bottom: 24px;
      left: 8px;
      z-index: 2;
      font-size: 10px;
      font-weight: 500;
      line-height: 1.3;
      color: $white;
    }

    &-date {
      position: absolute;
      bottom: 8px;
      left: 8px;
      z-index: 2;
      font-size: 8px;
      font-weight: 400;
    }

    &-title {
      font-size: 14px;
      font-weight: 600;
      color: $grey-black;
      margin-bottom: 8px;
    }

    &-subtitle {
      font-size: 16px;
      font-weight: 600;
      color: $black;
      line-height: 1.4;
      @include line-clamp(3);
    }
  }

  &__control {
    position: absolute;
    top: calc(50% - 20px);
    transform: translateY(10%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: $blue-night;
    cursor: pointer;
    padding: 0;

    &--prev {
      left: 0px !important;
    }

    &--next {
      right: 0px !important;
    }

    .e-icon {
      width: 24px;
      height: 24px;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &__card {
      &-bg {
        width: 160px;
      }
    }

    &__control {
      &--prev {
        left: 16px !important;
      }

      &--next {
        right: 16px !important;
      }
    }
  }
}
</style>
